import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface LocationConfirmationModalProps {
  onConfirm: (location: { latitude: number; longitude: number; accuracy?: number }) => void;
  onCancel: () => void;
  patientName: string;
}

type LocationState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; latitude: number; longitude: number; accuracy?: number; address?: string }
  | { status: 'error'; message: string };

const LocationConfirmationModal: React.FC<LocationConfirmationModalProps> = ({
  onConfirm,
  onCancel,
  patientName,
}) => {
  const [locationState, setLocationState] = useState<LocationState>({ status: 'idle' });

  const requestLocation = () => {
    if ('geolocation' in navigator) {
      setLocationState({ status: 'loading' });

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;
          const accuracy = position.coords.accuracy;

          // Try to get human-readable address using reverse geocoding
          let address: string | undefined;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            if (data.display_name) {
              address = data.display_name;
            }
          } catch (error) {
            console.log('Could not fetch address, will show coordinates only');
          }

          setLocationState({
            status: 'success',
            latitude,
            longitude,
            accuracy,
            address,
          });
        },
        (error) => {
          let message = 'Unable to get your location.';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'Location permission denied. Please enable location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              message = 'Location information is unavailable. Make sure location services are enabled on your device.';
              break;
            case error.TIMEOUT:
              message = 'Location request timed out. Please try again.';
              break;
          }
          setLocationState({ status: 'error', message });
        },
        {
          enableHighAccuracy: true,
          timeout: 15000, // Increased timeout for better accuracy
          maximumAge: 0, // Don't use cached location
        }
      );
    } else {
      setLocationState({
        status: 'error',
        message: 'Geolocation is not supported by your browser.',
      });
    }
  };

  useEffect(() => {
    // Request location on mount
    requestLocation();
  }, []);

  const handleConfirm = () => {
    if (locationState.status === 'success') {
      onConfirm({
        latitude: locationState.latitude,
        longitude: locationState.longitude,
        accuracy: locationState.accuracy,
      });
    }
  };

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000, // Higher than video session modal
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="location-modal-title"
        aria-describedby="location-modal-description"
        style={{
          background: '#16325B',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '700px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}>
          <h3 id="location-modal-title" style={{ margin: 0, color: '#f9fafb', fontSize: '1.5rem' }}>
            Confirm Your Location
          </h3>
          <button
            onClick={onCancel}
            aria-label="Close location confirmation"
            style={{
              background: 'none',
              border: 'none',
              color: '#d1d5db',
              cursor: 'pointer',
              fontSize: '24px',
              padding: 0,
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {/* Content */}
        <div style={{ marginBottom: '24px' }}>
          <p id="location-modal-description" style={{ color: '#d1d5db', fontSize: '16px', lineHeight: '1.5', marginBottom: '16px' }}>
            Before joining your video session, please confirm your current location.
            This helps ensure compliance with telehealth regulations.
          </p>

          {/* Location Status */}
          {locationState.status === 'loading' && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: '#1e3a5f',
              borderRadius: '8px',
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #374151',
                borderTop: '4px solid #60a5fa',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px',
              }} />
              <p style={{ color: '#d1d5db', margin: 0 }}>Getting your location...</p>
            </div>
          )}

          {locationState.status === 'error' && (
            <div style={{
              padding: '20px',
              background: '#7f1d1d',
              borderRadius: '8px',
              border: '1px solid #dc2626',
            }}>
              <p style={{ color: '#fecaca', margin: 0, fontSize: '14px' }}>
                <strong>Error:</strong> {locationState.message}
              </p>
              <p style={{ color: '#fecaca', margin: '12px 0 0 0', fontSize: '14px' }}>
                You may need to enable location services in your device settings or browser.
              </p>
            </div>
          )}

          {locationState.status === 'success' && (
            <div>
              {/* Map Display */}
              <div style={{
                height: '300px',
                borderRadius: '8px',
                overflow: 'hidden',
                marginBottom: '16px',
                border: '2px solid #374151',
              }}>
                <MapContainer
                  center={[locationState.latitude, locationState.longitude]}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[locationState.latitude, locationState.longitude]}>
                    <Popup>Your current location</Popup>
                  </Marker>
                </MapContainer>
              </div>

              {/* Location Details */}
              <div style={{
                background: '#1e3a5f',
                borderRadius: '8px',
                padding: '16px',
              }}>
                {locationState.address ? (
                  <>
                    {/* Show Address */}
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>
                        Your Location:
                      </div>
                      <div style={{ color: '#f9fafb', fontSize: '15px', lineHeight: '1.5' }}>
                        {locationState.address}
                      </div>
                    </div>
                    {locationState.accuracy && locationState.accuracy < 100 && (
                      <div style={{
                        color: '#10b981',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}>
                        <span>✓</span>
                        <span>Accurate to ±{Math.round(locationState.accuracy)} meters</span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Fallback to coordinates if address unavailable */}
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: '#d1d5db', fontSize: '14px' }}>Latitude: </span>
                      <span style={{ color: '#f9fafb', fontSize: '14px', fontFamily: 'monospace' }}>
                        {locationState.latitude.toFixed(6)}
                      </span>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: '#d1d5db', fontSize: '14px' }}>Longitude: </span>
                      <span style={{ color: '#f9fafb', fontSize: '14px', fontFamily: 'monospace' }}>
                        {locationState.longitude.toFixed(6)}
                      </span>
                    </div>
                    {locationState.accuracy && (
                      <div>
                        <span style={{ color: '#d1d5db', fontSize: '14px' }}>Accuracy: </span>
                        <span style={{ color: '#f9fafb', fontSize: '14px' }}>
                          ±{Math.round(locationState.accuracy)} meters
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              background: '#374151',
              border: 'none',
              borderRadius: '4px',
              padding: '12px 24px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={locationState.status !== 'success'}
            style={{
              background: locationState.status === 'success' ? '#10b981' : '#6b7280',
              border: 'none',
              borderRadius: '4px',
              padding: '12px 24px',
              color: 'white',
              cursor: locationState.status === 'success' ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              opacity: locationState.status === 'success' ? 1 : 0.6,
            }}
          >
            Confirm Location & Join Session
          </button>
        </div>
      </div>

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LocationConfirmationModal;
